import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Accessibility as AccessIcon,
  X,
  RotateCcw,
  Type,
  AlignJustify,
  Contrast,
  Sun,
  Moon,
  Eye,
  Link2,
  Heading1,
  MousePointer2,
  PauseCircle,
  BookOpen,
  Ban,
  Keyboard,
  FileText,
} from "lucide-react";

const HTML = () => document.documentElement;

const TOGGLES = [
  { key: "acc-grayscale", label: "Grayscale", icon: Ban },
  { key: "acc-high-contrast", label: "High Contrast", icon: Contrast },
  { key: "acc-negative", label: "Invert Colors", icon: Moon },
  { key: "acc-light-bg", label: "Light Background", icon: Sun },
  { key: "acc-links-underline", label: "Underline Links", icon: Link2 },
  { key: "acc-highlight-links", label: "Highlight Links", icon: Eye },
  { key: "acc-highlight-heading", label: "Highlight Titles", icon: Heading1 },
  { key: "acc-readable-font", label: "Readable Font", icon: Type },
  { key: "acc-dyslexia", label: "Dyslexia Font", icon: BookOpen },
  { key: "acc-big-cursor", label: "Big Cursor", icon: MousePointer2 },
  { key: "acc-pause-animations", label: "Pause Motion", icon: PauseCircle },
  { key: "acc-focus", label: "Focus Outline", icon: Keyboard },
];

const DEFAULT = {
  toggles: {},
  fontScale: 100,
  spacing: 0, // 0,1,2
  readingGuide: false,
};

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(DEFAULT);
  const guideRef = useRef(null);
  const panelRef = useRef(null);

  // Load persisted profile
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("mm-accessibility") || "null");
      if (saved) setState({ ...DEFAULT, ...saved });
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Apply state to <html>
  useEffect(() => {
    const el = HTML();
    TOGGLES.forEach((t) => el.classList.toggle(t.key, !!state.toggles[t.key]));
    el.classList.remove("acc-spacing-1", "acc-spacing-2");
    if (state.spacing === 1) el.classList.add("acc-spacing-1");
    if (state.spacing === 2) el.classList.add("acc-spacing-2");
    el.style.fontSize = state.fontScale === 100 ? "" : `${state.fontScale}%`;
    try {
      localStorage.setItem("mm-accessibility", JSON.stringify(state));
    } catch (e) {
      /* ignore */
    }
  }, [state]);

  // Reading guide follows pointer
  const onMove = useCallback((e) => {
    if (guideRef.current) guideRef.current.style.top = `${e.clientY}px`;
  }, []);
  useEffect(() => {
    if (state.readingGuide) {
      window.addEventListener("mousemove", onMove);
      return () => window.removeEventListener("mousemove", onMove);
    }
  }, [state.readingGuide, onMove]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggle = (key) =>
    setState((s) => ({ ...s, toggles: { ...s.toggles, [key]: !s.toggles[key] } }));

  const reset = () => {
    setState(DEFAULT);
    const el = HTML();
    TOGGLES.forEach((t) => el.classList.remove(t.key));
    el.classList.remove("acc-spacing-1", "acc-spacing-2");
    el.style.fontSize = "";
  };

  const activeCount =
    Object.values(state.toggles).filter(Boolean).length +
    (state.fontScale !== 100 ? 1 : 0) +
    (state.spacing !== 0 ? 1 : 0) +
    (state.readingGuide ? 1 : 0);

  return (
    <>
      {state.readingGuide && <div ref={guideRef} className="acc-reading-guide" aria-hidden="true" />}

      {/* Floating trigger - bottom left */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open accessibility menu"
        aria-expanded={open}
        className="fixed left-4 bottom-[86px] lg:bottom-4 z-[2147483001] h-14 w-14 rounded-full bg-brand-ocean hover:bg-brand-sky text-white grid place-items-center shadow-[0_10px_30px_-6px_rgba(12,109,168,0.7)] ring-4 ring-white/70 transition-transform hover:scale-105 active:scale-95"
      >
        <AccessIcon className="h-7 w-7" />
        {activeCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-brand-gold text-brand-navy text-[11px] font-heading font-extrabold grid place-items-center ring-2 ring-white">
            {activeCount}
          </span>
        )}
      </button>

      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[2147483000] bg-black/40 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden={!open}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Accessibility tools"
        className={`fixed left-0 bottom-0 top-0 z-[2147483002] w-[92%] max-w-[380px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 h-16 bg-brand-navy text-white shrink-0">
          <div className="flex items-center gap-2">
            <AccessIcon className="h-6 w-6 text-brand-sky2" />
            <span className="font-heading font-extrabold text-lg">Accessibility</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={reset}
              className="flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 rounded-md px-2.5 py-1.5 transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
            <button onClick={() => setOpen(false)} aria-label="Close accessibility menu" className="h-9 w-9 grid place-items-center rounded-md bg-white/10 hover:bg-white/20 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Text size */}
          <section>
            <div className="flex items-center gap-2 mb-2 text-brand-navy font-heading font-bold text-sm">
              <Type className="h-4 w-4 text-brand-ocean" /> Text Size - {state.fontScale}%
            </div>
            <div className="flex items-center gap-2">
              <StepBtn onClick={() => setState((s) => ({ ...s, fontScale: Math.max(90, s.fontScale - 10) }))}>A−</StepBtn>
              <div className="flex-1 h-2 rounded-full bg-brand-mist overflow-hidden">
                <div className="h-full bg-brand-sky" style={{ width: `${((state.fontScale - 90) / 70) * 100}%` }} />
              </div>
              <StepBtn onClick={() => setState((s) => ({ ...s, fontScale: Math.min(160, s.fontScale + 10) }))}>A+</StepBtn>
            </div>
          </section>

          {/* Spacing */}
          <section>
            <div className="flex items-center gap-2 mb-2 text-brand-navy font-heading font-bold text-sm">
              <AlignJustify className="h-4 w-4 text-brand-ocean" /> Text Spacing
            </div>
            <div className="grid grid-cols-3 gap-2">
              {["Default", "Medium", "Large"].map((label, i) => (
                <button
                  key={label}
                  onClick={() => setState((s) => ({ ...s, spacing: i }))}
                  className={`h-10 rounded-lg text-xs font-heading font-bold transition-colors ${
                    state.spacing === i
                      ? "bg-brand-ocean text-white"
                      : "bg-brand-mist text-brand-navy hover:bg-brand-sky/20"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          {/* Reading guide */}
          <section>
            <button
              onClick={() => setState((s) => ({ ...s, readingGuide: !s.readingGuide }))}
              className={`w-full flex items-center justify-between rounded-xl border-2 px-4 h-14 transition-colors ${
                state.readingGuide ? "border-brand-ocean bg-brand-sky/10" : "border-brand-mist hover:border-brand-sky/40"
              }`}
            >
              <span className="flex items-center gap-2.5 font-heading font-bold text-sm text-brand-navy">
                <BookOpen className="h-5 w-5 text-brand-ocean" /> Reading Guide
              </span>
              <Switch on={state.readingGuide} />
            </button>
          </section>

          {/* Toggle grid */}
          <section>
            <div className="text-brand-navy font-heading font-bold text-sm mb-2">Display Adjustments</div>
            <div className="grid grid-cols-2 gap-2.5">
              {TOGGLES.map((t) => {
                const Icon = t.icon;
                const active = !!state.toggles[t.key];
                return (
                  <button
                    key={t.key}
                    onClick={() => toggle(t.key)}
                    aria-pressed={active}
                    className={`flex flex-col items-center justify-center gap-1.5 h-[84px] rounded-xl border-2 text-center px-2 transition-all ${
                      active
                        ? "border-brand-ocean bg-brand-ocean text-white shadow-glow"
                        : "border-brand-mist bg-white text-brand-navy hover:border-brand-sky/50"
                    }`}
                  >
                    <Icon className={`h-6 w-6 ${active ? "text-white" : "text-brand-ocean"}`} />
                    <span className="text-[11px] font-heading font-bold leading-tight">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <div className="shrink-0 border-t border-brand-mist p-4 bg-brand-mist/40">
          <Link
            to="/accessibility-statement"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-2 h-11 rounded-lg bg-brand-navy text-white font-heading font-bold text-sm hover:bg-brand-ocean transition-colors"
          >
            <FileText className="h-4 w-4" /> Accessibility Statement
          </Link>
          <p className="text-[11px] text-brand-slate text-center mt-2">
            WCAG 2.1 AA aligned · Preferences saved to this device
          </p>
        </div>
      </div>
    </>
  );
}

function StepBtn({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      className="h-10 w-12 rounded-lg bg-brand-navy text-white font-heading font-extrabold text-sm hover:bg-brand-ocean transition-colors"
    >
      {children}
    </button>
  );
}

function Switch({ on }) {
  return (
    <span className={`relative h-6 w-11 rounded-full transition-colors ${on ? "bg-brand-ocean" : "bg-brand-slate/40"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </span>
  );
}
